import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Campo } from '../componentes/ui/formulario';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Cargando, Problema, useTextoDeError } from '../componentes/ui/estados';
import { usePaqueteDeLaEmpresa, usePermiso } from '../sesion/ProveedorDeSesion';
import { ListasDePrecios } from '../componentes/ListasDePrecios';
import { ConceptosDeCaja } from '../componentes/ConceptosDeCaja';

interface UnRespaldo {
  readonly archivo: string;
  readonly tamano: number;
  readonly verificado: boolean;
  readonly subido: boolean | null;
  readonly detalle: string | null;
  readonly ocurridoEn: string;
  readonly haceHoras: number;
}

interface Parametro {
  readonly codigo: string;
  readonly modulo: string;
  readonly tipo: string;
  readonly clase: 'libre' | 'con_corte' | 'fijado_al_inicio';
  readonly claveDeTraduccion: string;
  readonly opciones: string[] | null;
  readonly valor: unknown;
  readonly esValorPorDefecto: boolean;
}

const TONO_POR_CLASE = {
  libre: 'neutro',
  con_corte: 'atencion',
  fijado_al_inicio: 'neutro',
} as const;

/**
 * Configuración del comercio.
 *
 * Lo distinto de esta pantalla es que **muestra por qué algo no se puede cambiar**. Un
 * campo deshabilitado sin explicación se lee como un error del sistema; con su motivo al
 * lado se lee como una decisión. Los parámetros que reinterpretan el pasado —el criterio
 * de costeo, cuándo se gana una comisión— quedan a la vista pero bloqueados hasta que
 * exista el proceso de recálculo con fecha de corte.
 */
interface FichaDelComercio {
  readonly nombre: string;
  readonly razonSocial: string;
  readonly tipoDeIdentificacion: string;
  readonly identificacion: string;
  readonly domicilio: string | null;
  readonly ciudad: string | null;
  readonly codigoPostal: string | null;
  readonly telefono: string | null;
  readonly correo: string | null;
}

const CAMPOS_DEL_COMERCIO = [
  'nombre',
  'razonSocial',
  'domicilio',
  'ciudad',
  'codigoPostal',
  'telefono',
  'correo',
] as const;
type CampoDelComercio = (typeof CAMPOS_DEL_COMERCIO)[number];
const ETIQUETA: Record<CampoDelComercio, string> = {
  nombre: 'alta.nombre',
  razonSocial: 'alta.razonSocial',
  domicilio: 'plataforma.domicilio',
  ciudad: 'plataforma.ciudad',
  codigoPostal: 'plataforma.codigoPostal',
  telefono: 'plataforma.telefono',
  correo: 'plataforma.correoDelComercio',
};

/**
 * Los datos del propio comercio, para corregirlos desde adentro.
 *
 * Hasta acá un comercio no podía cambiar su nombre ni su domicilio después de fundarse. La
 * identificación y la condición frente al IVA siguen sin poder cambiarse desde acá, y la pantalla
 * lo dice: lo ya facturado las lleva.
 */
function DatosDelComercio(): React.ReactElement | null {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const puedeVer = usePermiso('EMPRESA.EMPRESA.VER');
  const puedeEditar = usePermiso('EMPRESA.EMPRESA.EDITAR');
  const paquete = usePaqueteDeLaEmpresa();
  const [edicion, setEdicion] = useState<Partial<Record<CampoDelComercio, string>>>({});
  const [error, setError] = useState<string | null>(null);

  const ficha = useQuery({
    queryKey: ['empresa-actual'],
    enabled: puedeVer,
    queryFn: () => pedir<FichaDelComercio>('/empresas/actual'),
  });
  const valorDe = (campo: CampoDelComercio): string =>
    edicion[campo] ?? String(ficha.data?.[campo] ?? '');

  const guardar = useMutation({
    mutationFn: () => {
      const cambios: Partial<Record<CampoDelComercio, string>> = {};
      for (const campo of CAMPOS_DEL_COMERCIO) {
        const escrito = edicion[campo];
        if (escrito !== undefined && escrito.trim() !== String(ficha.data?.[campo] ?? '')) {
          cambios[campo] = escrito.trim();
        }
      }
      return pedir<void>('/empresas/actual', { method: 'PATCH', cuerpo: cambios });
    },
    onSuccess: async () => {
      setEdicion({});
      setError(null);
      await cola.invalidateQueries({ queryKey: ['empresa-actual'] });
      // El nombre del comercio está en la barra de arriba: sale del perfil.
      await cola.invalidateQueries({ queryKey: ['perfil'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  if (!puedeVer || !ficha.isSuccess) return null;

  return (
    <Tarjeta
      titulo={t('configuracion.datosDelComercio')}
      descripcion={t('configuracion.ayudaDatosDelComercio')}
      acciones={
        puedeEditar ? (
          <Boton
            tono="principal"
            cargando={guardar.isPending}
            onClick={() => {
              guardar.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-apoyo text-texto-tenue">
          {ficha.data.tipoDeIdentificacion}{' '}
          {paquete?.formatearIdentificacion(
            ficha.data.tipoDeIdentificacion,
            ficha.data.identificacion,
          ) ?? ficha.data.identificacion}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {CAMPOS_DEL_COMERCIO.map((campo) => (
            <Campo
              key={campo}
              etiqueta={t(ETIQUETA[campo])}
              disabled={!puedeEditar}
              value={valorDe(campo)}
              onChange={(evento) => {
                setEdicion({ ...edicion, [campo]: evento.target.value });
              }}
            />
          ))}
        </div>
        {error === null ? null : (
          <p role="alert" className="text-xs text-mal">
            {error}
          </p>
        )}
      </div>
    </Tarjeta>
  );
}

export function Configuracion(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();
  const puedeEditar = usePermiso('EMPRESA.CONFIGURACION.EDITAR');
  const puedeVerLaCaja = usePermiso('CAJA.TURNO.VER');
  const [error, setError] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['parametros'],
    queryFn: () => pedir<Parametro[]>('/parametros'),
  });

  const respaldos = useQuery({
    queryKey: ['respaldos'],
    queryFn: () => pedir<UnRespaldo[]>('/respaldos'),
  });

  const fijar = useMutation({
    mutationFn: ({ codigo, valor }: { codigo: string; valor: unknown }) =>
      pedir<void>(`/parametros/${codigo}`, { method: 'PUT', cuerpo: { valor } }),
    onSuccess: async () => {
      setError(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['parametros'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const porModulo = new Map<string, Parametro[]>();
  for (const parametro of consulta.data ?? []) {
    porModulo.set(parametro.modulo, [...(porModulo.get(parametro.modulo) ?? []), parametro]);
  }

  const editable = (parametro: Parametro): boolean => puedeEditar && parametro.clase === 'libre';

  const control = (parametro: Parametro): React.ReactElement => {
    const deshabilitado = !editable(parametro);

    if (parametro.tipo === 'booleano') {
      return (
        <input
          type="checkbox"
          checked={parametro.valor === true}
          disabled={deshabilitado}
          aria-label={t(parametro.claveDeTraduccion, { defaultValue: parametro.codigo })}
          onChange={(evento) => {
            fijar.mutate({ codigo: parametro.codigo, valor: evento.target.checked });
          }}
        />
      );
    }

    if (parametro.tipo === 'opcion') {
      return (
        <select
          value={String(parametro.valor)}
          disabled={deshabilitado}
          aria-label={t(parametro.claveDeTraduccion, { defaultValue: parametro.codigo })}
          onChange={(evento) => {
            fijar.mutate({ codigo: parametro.codigo, valor: evento.target.value });
          }}
          className="rounded-marco border border-borde bg-transparent px-2 py-1 text-sm disabled:opacity-50"
        >
          {(parametro.opciones ?? []).map((opcion) => (
            <option key={opcion} value={opcion}>
              {t(`opciones.${opcion}`, { defaultValue: opcion })}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={parametro.tipo === 'entero' ? 'number' : 'text'}
        defaultValue={String(parametro.valor)}
        disabled={deshabilitado}
        aria-label={t(parametro.claveDeTraduccion, { defaultValue: parametro.codigo })}
        onBlur={(evento) => {
          const valor =
            parametro.tipo === 'entero' ? Number(evento.target.value) : evento.target.value;
          if (String(valor) !== String(parametro.valor)) {
            fijar.mutate({ codigo: parametro.codigo, valor });
          }
        }}
        className="w-28 rounded-marco border border-borde bg-transparent px-2 py-1 text-right text-sm disabled:opacity-50"
      />
    );
  };

  return (
    <>
      <Encabezado titulo={t('configuracion.titulo')} descripcion={t('configuracion.descripcion')} />

      {/* El respaldo va arriba de todo y no escondido al final. Es lo único de esta pantalla que
          puede estar mal **sin que nadie haga nada**, y la diferencia entre un sistema que respalda
          todos los días y uno que dejó de hacerlo hace tres semanas se descubre el peor día
          posible. */}
      {respaldos.data === undefined ? null : (
        <Tarjeta>
          <h2 className="mb-2 text-sm font-semibold">{t('respaldos.titulo')}</h2>
          {respaldos.data.length === 0 ? (
            <p className="text-sm text-mal">{t('respaldos.ninguno')}</p>
          ) : (
            <>
              {(() => {
                const ultimo = respaldos.data[0];
                if (ultimo === undefined) return null;
                return (
                  <p className="flex flex-wrap items-center gap-2 text-sm">
                    <span>
                      {t('respaldos.ultimo', {
                        cuando: new Date(ultimo.ocurridoEn).toLocaleString(),
                      })}
                    </span>
                    {/* Dos días sin respaldo no es una curiosidad: es el aviso. Las horas las
                        cuenta el servidor: un mostrador con la hora mal puesta mostraría una
                        alarma falsa, o peor, no mostraría la verdadera. */}
                    <Insignia
                      tono={ultimo.haceHoras >= 48 ? 'mal' : ultimo.verificado ? 'bien' : 'mal'}
                    >
                      {ultimo.verificado ? t('respaldos.verificado') : t('respaldos.sinVerificar')}
                    </Insignia>
                    {/* Nulo es "no hay destino configurado", que no es una falla pero sí algo que
                        quien administra el sistema tiene que saber que eligió. */}
                    <Insignia
                      tono={
                        ultimo.subido === true
                          ? 'bien'
                          : ultimo.subido === false
                            ? 'mal'
                            : 'atencion'
                      }
                    >
                      {ultimo.subido === true
                        ? t('respaldos.afuera')
                        : ultimo.subido === false
                          ? t('respaldos.noSubio')
                          : t('respaldos.soloAca')}
                    </Insignia>
                  </p>
                );
              })()}
              <ul className="mt-2 flex flex-col gap-0.5 text-xs text-texto-tenue">
                {respaldos.data.slice(1, 6).map((uno) => (
                  <li key={uno.archivo} className="flex gap-2">
                    <span className="numero">{new Date(uno.ocurridoEn).toLocaleDateString()}</span>
                    <span>{uno.verificado ? t('respaldos.verificado') : uno.detalle}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <DatosDelComercio />
        </Tarjeta>
      )}

      {/* Las listas de precios (E36): con qué precios se vende, y desde y hasta cuándo. */}
      <ListasDePrecios />

      {/* Los conceptos de caja (E37): por qué entra o sale plata del cajón, y a qué cuenta va. */}
      {puedeVerLaCaja ? <ConceptosDeCaja /> : null}

      {consulta.isPending ? <Cargando /> : null}
      {consulta.isError ? (
        <Problema
          error={consulta.error}
          reintentar={() => {
            void consulta.refetch();
          }}
        />
      ) : null}

      {error === null ? null : (
        <p role="alert" className="mb-3 text-sm text-mal">
          {error}
        </p>
      )}

      <div className="flex flex-col gap-4">
        {[...porModulo.entries()].map(([modulo, parametros]) => (
          <Tarjeta key={modulo} titulo={t(`modulos.${modulo}`, { defaultValue: modulo })}>
            <dl className="flex flex-col gap-3">
              {parametros.map((parametro) => (
                <div
                  key={parametro.codigo}
                  className="flex items-start justify-between gap-4 border-b border-borde pb-3 last:border-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <dt className="text-sm">
                      {t(parametro.claveDeTraduccion, { defaultValue: parametro.codigo })}
                    </dt>
                    <dd className="mt-0.5 flex flex-wrap items-center gap-2">
                      {parametro.clase === 'libre' ? null : (
                        <Insignia tono={TONO_POR_CLASE[parametro.clase]}>
                          {t(`configuracion.clases.${parametro.clase}`)}
                        </Insignia>
                      )}
                      <span className="text-xs text-texto-tenue">
                        {t(`configuracion.motivos.${parametro.clase}`)}
                      </span>
                    </dd>
                  </div>
                  <div className="shrink-0">{control(parametro)}</div>
                </div>
              ))}
            </dl>
          </Tarjeta>
        ))}
      </div>
    </>
  );
}
